/* Public enterprise-facing Security Package page — recipient-scoped, NDA-gated,
   watermarked, expiring. Polished for procurement/security review audiences. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Lock, FileText, BadgeCheck, Send, Download, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-cobalt/30";

const Section = ({ title, children, tid }) => (
  <div className="rounded-3xl bg-white border border-slate-100 p-6" data-testid={tid}>
    <h3 className="font-heading font-bold text-navy text-sm uppercase tracking-wide">{title}</h3>
    <div className="mt-2 text-sm text-slate-600">{children}</div>
  </div>
);

export default function SecurityPackagePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [ndaName, setNdaName] = useState("");
  const [pkg, setPkg] = useState(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/security-package/${token}`)
      .then(({ data }) => setMeta(data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid."));
  }, [token]);

  const acceptNda = async () => {
    if (!ndaName || !email) { toast.error("Enter your name and registered email"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/security-package/${token}/accept-nda`, { name: ndaName, email });
      setMeta({ ...meta, nda_accepted: true });
      toast.success("NDA accepted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const open = async (action = "view") => {
    if (!email) { toast.error("Confirm your registered email"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/security-package/${token}/${action}`, { email });
      setPkg(data);
      if (action === "download") {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "crash_ready_security_package.json"; a.click();
        toast.success("Package downloaded (logged)");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Access denied"); } finally { setBusy(false); }
  };

  const ask = async () => {
    if (!question.trim()) return;
    try {
      await axios.post(`${API}/public/security-package/${token}/questions`, { text: question });
      toast.success("Question sent to the Crash Ready security team");
      setQuestion("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="rounded-3xl bg-white border border-slate-100 p-8 text-center max-w-md" data-testid="spkg-public-error">
        <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
        <p className="mt-3 text-slate-600 text-sm">{error}</p>
      </div>
    </div>
  );
  if (!meta) return <div className="min-h-screen bg-slate-50" />;

  const c = pkg?.content || {};
  return (
    <div className="min-h-screen bg-slate-50" data-testid="security-package-page">
      <header className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-center gap-2 text-cobalt font-heading font-extrabold tracking-tight"><ShieldCheck className="w-6 h-6" /> CRASH READY</div>
          <h1 className="font-heading text-2xl font-extrabold mt-2" data-testid="spkg-public-title">Security Package</h1>
          <p className="text-white/60 text-xs mt-1" data-testid="spkg-public-recipient">
            Prepared exclusively for <b className="text-white">{meta.recipient_name}</b> · {meta.organization} · Purpose: {meta.purpose?.replace(/_/g, " ")} · Expires {meta.expires_at?.slice(0, 10)}
          </p>
          {meta.superseded && <p className="mt-2 text-[11px] text-amber-300">An updated package version exists — contact your Crash Ready representative for the latest.</p>}
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        {!pkg && (
          <div className="rounded-3xl bg-white border border-slate-100 p-6" data-testid="spkg-public-gate">
            <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Lock className="w-4 h-4 text-cobalt" /> Verify your access</h3>
            <div className="mt-3 max-w-md space-y-2">
              <input placeholder="Your registered email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="spkg-public-email" />
              {meta.nda_required && !meta.nda_accepted && (
                <>
                  <div className="rounded-xl bg-slate-50 p-3 text-[11px] text-slate-500 max-h-32 overflow-auto" data-testid="spkg-public-nda-text">{meta.nda_text}</div>
                  <input placeholder="Full name (electronic NDA signature)" value={ndaName} onChange={(e) => setNdaName(e.target.value)} className={inputCls} data-testid="spkg-public-nda-name" />
                  <button onClick={acceptNda} disabled={busy} className="px-5 py-2.5 rounded-full bg-navy text-white text-xs font-bold w-full" data-testid="spkg-public-nda-accept">Accept NDA &amp; continue</button>
                </>
              )}
              {(!meta.nda_required || meta.nda_accepted) && (
                <button onClick={() => open("view")} disabled={busy} className="px-5 py-2.5 rounded-full bg-navy text-white text-xs font-bold w-full" data-testid="spkg-public-open">Open security package</button>
              )}
            </div>
          </div>
        )}
        {pkg && (
          <>
            <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-2 text-[10px] text-amber-700 font-bold" data-testid="spkg-public-watermark">{pkg.watermark}</div>
            <div className="flex flex-wrap gap-2 text-[10px]">
              <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-bold" data-testid="spkg-public-hash"><BadgeCheck className="w-3 h-3 inline mr-0.5" /> Integrity SHA-256 verified · {pkg.sha256?.slice(0, 16)}…</span>
              <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 font-bold">v{pkg.version} · generated {pkg.generated_at?.slice(0, 10)}</span>
              {meta.permission === "download" && <button onClick={() => open("download")} className="px-2.5 py-1 rounded-full bg-navy text-white font-bold" data-testid="spkg-public-download"><Download className="w-3 h-3 inline mr-0.5" /> Download (logged)</button>}
            </div>
            {c.executive_overview && <Section title="Executive security overview" tid="spkg-sec-overview">{c.executive_overview}</Section>}
            {c.phase_gate_summary && (
              <Section title="Production security gate" tid="spkg-sec-gate">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold">{c.phase_gate_summary.title}</span>
                  <span className="text-[11px] text-slate-400">Re-audit {c.phase_gate_summary.reaudit_date?.slice(0, 10)} · {c.phase_gate_summary.status} · SHA-256 {c.phase_gate_summary.sha256_verification}</span>
                </div>
                <ul className="mt-2 space-y-1 text-[12px]">
                  {(c.phase_gate_summary.findings_external_summary || []).map((f, i) => (
                    <li key={i} className="rounded-lg bg-slate-50 px-3 py-1.5">{f.severity}: {f.summary} — <span className="text-emerald-700">{f.remediation} ({f.status})</span></li>
                  ))}
                </ul>
                <p className="text-[10px] text-slate-400 mt-2">{c.phase_gate_summary.note}</p>
              </Section>
            )}
            {c.control_readiness && <Section title="Control readiness" tid="spkg-sec-controls">{c.control_readiness.total_controls} controls tracked · {Object.entries(c.control_readiness.by_result || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}<p className="text-[10px] text-slate-400 mt-1">{c.control_readiness.honest_scoring}</p></Section>}
            {c.framework_mappings && <Section title="Framework mappings" tid="spkg-sec-frameworks"><ul className="grid md:grid-cols-2 gap-1 text-[12px]">{Object.entries(c.framework_mappings).map(([k, v]) => <li key={k} className="rounded-lg bg-slate-50 px-3 py-1.5"><b className="text-navy uppercase">{k.replace(/_/g, " ")}</b> — {v}</li>)}</ul></Section>}
            {[["Access control", c.access_control, "spkg-sec-access"], ["Encryption", c.encryption, "spkg-sec-encryption"], ["Incident response", c.incident_response, "spkg-sec-ir"], ["Vendor risk", c.vendor_risk, "spkg-sec-vendor"], ["Business continuity", c.business_continuity, "spkg-sec-bc"]].map(([t, v, tid]) => v && <Section key={t} title={t} tid={tid}>{v}</Section>)}
            {c.policy_index && <Section title="Policy index" tid="spkg-sec-policies"><ul className="grid md:grid-cols-2 gap-1 text-[12px]">{c.policy_index.map((p) => <li key={p} className="flex items-center gap-1.5"><FileText className="w-3 h-3 text-cobalt" /> {p}</li>)}</ul></Section>}
            {(pkg.documents || []).length > 0 && <Section title="Approved documents" tid="spkg-sec-docs"><ul className="space-y-1 text-[12px]">{pkg.documents.map((d) => <li key={d.doc_id} className="rounded-lg bg-slate-50 px-3 py-1.5">{d.title} (v{d.version}) · sha256 {d.sha256?.slice(0, 12)}…</li>)}</ul></Section>}
            <Section title="External certification status" tid="spkg-sec-certs">
              {Array.isArray(c.certification_status) ? c.certification_status.map((x) => `${x.name} — ${x.issuer}`).join(" · ") : c.certification_status}
            </Section>
            {c.limitations && <Section title="Current limitations" tid="spkg-sec-limits"><ul className="list-disc pl-4 space-y-1 text-[12px]">{c.limitations.map((l) => <li key={l}>{l}</li>)}</ul></Section>}
            <Section title="Questions & additional documents" tid="spkg-sec-questions">
              <div className="flex gap-2">
                <input placeholder="Ask a security question or request an additional document…" value={question} onChange={(e) => setQuestion(e.target.value)} className={inputCls} data-testid="spkg-public-question-input" />
                <button onClick={ask} className="px-4 rounded-full bg-navy text-white text-xs font-bold shrink-0" data-testid="spkg-public-question-send"><Send className="w-3.5 h-3.5" /></button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Security contact: {c.security_contact?.email || "security@crashready.com"}</p>
            </Section>
          </>
        )}
        <p className="text-[10px] text-slate-400 pb-8" data-testid="spkg-public-disclaimer">{meta.disclaimer}</p>
      </div>
    </div>
  );
}
